import axiosInstance from "../axiosInstance";
import { getEnv } from "../environment.js";
import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Container,
  Pagination,
  Spinner,
  Row,
  Col,
  Form,
  Dropdown,
  Tabs,
  Tab,
} from "react-bootstrap";
import "../styles/MetricsTable.css";
import Icon from "../components/generic/Icon";
import ExportToCSV from "../components/generic/ExportToCSV";
import MetricsVisual from "./MetricsVisual";

// Set API endpoint
const env = getEnv();
const api_url = env.url;
console.log(api_url);

const CLUSTER_LABEL_MAP = {
  "op-epg101im1.gm.com": "Dev Milford",
  "op17wi.gm.com": "Prod Warren",
  "op44im1.gm.com": "Prod Milford",
};

const CLUSTER_REVERSE_MAP = Object.fromEntries(
  Object.entries(CLUSTER_LABEL_MAP).map(([domain, label]) => [label, domain]),
);

const PARAM_KEY_MAP = {
  request_asms: "request_asms",
  request_client_id: "client_id",
  request_gmid: "gmid",
  request_method: "request_method",
  content_type: "content_type",
  model_name: "model_name",
  cluster: "cluster",
  endpoint: "endpoint",
  http_status: "http_status",
};

const normalizeFilterValues = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) {
    return data
      .map((item) => (item == null ? "" : String(item).trim()))
      .filter(Boolean);
  }
  if (typeof data === "object") {
    return Object.keys(data)
      .map((key) => (key == null ? "" : String(key).trim()))
      .filter(Boolean);
  }
  return [String(data).trim()].filter(Boolean);
};

function Metrics() {
  // State for backend-provided filter options
  const [allRequestAsms, setAllRequestAsms] = useState([]);
  const [allClientIds, setAllClientIds] = useState([]);
  const [allGmids, setAllGmids] = useState([]);
  const [modelFilterOptions, setModelFilterOptions] = useState([]);
  const [clusterFilterOptions, setClusterFilterOptions] = useState([]);
  const [endpointFilterOptions, setEndpointFilterOptions] = useState([]);
  // Single Metrics page with tabs (default to Graph View)
  const [activeTab, setActiveTab] = useState("graphs");
  const [isGatewayLoaded, setIsGatewayLoaded] = useState(false);
  const [isTgiLoaded, setIsTgiLoaded] = useState(false);
  const [tgiMetrics, setTgiMetrics] = useState([]);
  const [gatewayMetrics, setGatewayMetrics] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState({
    request_time: { start: "", end: "" },
    request_asms: [],
    request_client_id: [],
    request_gmid: [],
    request_method: [],
    content_type: [],
    model_name: [],
    cluster: [],
    endpoint: [],
    http_status: [],
  });
  const [openDropdown, setOpenDropdown] = useState(null);
  const [globalSearchInput, setGlobalSearchInput] = useState("");
  const [globalSearch, setGlobalSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [tgiCurrentPage, setTgiCurrentPage] = useState(1);
  const [tgiItemsPerPage, setTgiItemsPerPage] = useState(10);

  const exportRef = useRef();
  const searchInputRef = useRef(null);

  const handleExport = () => {
    exportRef.current.exportToCSV();
  };

  // Trigger search when user presses Enter or clicks search button
  const triggerSearch = () => {
    setCurrentPage(1);
    setGlobalSearch(globalSearchInput.trim());
  };

  // Clear search input and reset search state
  const clearSearch = () => {
    if (!globalSearchInput && !globalSearch) return;
    setGlobalSearchInput("");
    setGlobalSearch("");
    setCurrentPage(1);
    setTimeout(() => searchInputRef.current?.focus(), 0);
  };

  // Fetch TGI metrics data (preload)
  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const response = await axiosInstance.get(`${api_url}/get-tgi-metrics`);
        console.log("TGI Metrics Response:", response.data);
        setTgiMetrics(response.data);
      } catch (error) {
        console.error(`Error fetching TGI metrics:`, error);
      } finally {
        setIsTgiLoaded(true);
      }
    };
    fetchMetrics();
  }, []);

  // Helper to flatten selectedOptions for backend params
  const buildFilterParams = React.useCallback(() => {
    const params = {
      page: currentPage,
      limit: itemsPerPage,
    };
    // Add global search if present
    if (globalSearch.trim() !== "") {
      params["search"] = globalSearch;
    }
    // Add each filter if it has a value
    Object.entries(selectedOptions).forEach(([key, value]) => {
      if (key === "request_time") {
        if (value.start) params["startDate"] = value.start;
        if (value.end) params["endDate"] = value.end;
      } else if (Array.isArray(value) && value.length > 0) {
        let normalizedValue = value;
        if (key === "cluster") {
          normalizedValue = value.map(
            (clusterLabel) => CLUSTER_REVERSE_MAP[clusterLabel] || clusterLabel,
          );
        }
        const joinedValue = normalizedValue.join(",");
        const backendKey = PARAM_KEY_MAP[key] || key;
        params[backendKey] = joinedValue;
      }
    });
    return params;
  }, [currentPage, itemsPerPage, selectedOptions, globalSearch]);

  // Fetch gateway metrics with filters
  useEffect(() => {
    let isCurrent = true;
    setIsGatewayLoaded(false);
    const fetchMetrics = async () => {
      try {
        const params = buildFilterParams();
        const url = new URL(`${api_url}/get-gateway-metrics-advanced`);
        Object.entries(params).forEach(([key, value]) =>
          url.searchParams.append(key, value),
        );
        console.log("Full API URL:", url.toString());
        const response = await axiosInstance.get(
          `${api_url}/get-gateway-metrics-advanced`,
          {
            params,
          },
        );
        const responseData = response.data || {};
        console.log("Gateway Metrics Response:", responseData);

        const total = Number(responseData.total ?? 0);
        const maxPage = Math.max(1, Math.ceil(total / itemsPerPage));
        if (currentPage !== 1 && (total === 0 || currentPage > maxPage)) {
          if (isCurrent) {
            setTotalCount(total);
            setIsGatewayLoaded(true);
            setCurrentPage(1);
          }
          return;
        }

        const enrichedData = (responseData.data || []).map((item) => {
          const { modelName, clusterLabel, clusterDomain, endpoint } =
            parseTargetUrl(item.target_url);
          return {
            ...item,
            derivedModelName: modelName,
            derivedClusterLabel: clusterLabel,
            derivedClusterDomain: clusterDomain,
            derivedEndpoint: endpoint,
          };
        });
        if (isCurrent) {
          setGatewayMetrics(enrichedData);
          const filtersPayload = {
            ...(responseData.filters || {}),
            requestAsms:
              (responseData.filters || {}).requestAsms ??
              responseData.requestAsms,
            clientIds:
              (responseData.filters || {}).clientIds ?? responseData.clientIds,
            gmid: (responseData.filters || {}).gmid ?? responseData.gmids,
            model: (responseData.filters || {}).model ?? responseData.models,
            cluster:
              (responseData.filters || {}).cluster ?? responseData.clusters,
            endpoint:
              (responseData.filters || {}).endpoint ?? responseData.endpoints,
          };

          const requestAsmsList = normalizeFilterValues(
            filtersPayload.requestAsms || filtersPayload.request_asms,
          );
          const clientIdList = normalizeFilterValues(
            filtersPayload.clientIds || filtersPayload.client_ids,
          );
          const gmidList = normalizeFilterValues(
            filtersPayload.gmid || filtersPayload.gmids,
          );
          const modelList = normalizeFilterValues(
            filtersPayload.model || filtersPayload.models,
          );

          const clusterRawList = normalizeFilterValues(
            filtersPayload.cluster || filtersPayload.clusters,
          );
          const clusterLabels = Array.from(
            new Set(
              clusterRawList.map((value) => CLUSTER_LABEL_MAP[value] || value),
            ),
          );

          const endpointList = normalizeFilterValues(
            filtersPayload.endpoint || filtersPayload.endpoints,
          );

          const fallbackRequestAsms = normalizeFilterValues(
            responseData.allRequestAsms || responseData.requestAsms,
          );
          const fallbackClientIds = normalizeFilterValues(
            responseData.allClientIds || responseData.clientIds,
          );
          const fallbackGmids = normalizeFilterValues(
            responseData.allGmids || responseData.gmids,
          );

          setAllRequestAsms(
            requestAsmsList.length > 0 ? requestAsmsList : fallbackRequestAsms,
          );
          setAllClientIds(
            clientIdList.length > 0 ? clientIdList : fallbackClientIds,
          );
          setAllGmids(gmidList.length > 0 ? gmidList : fallbackGmids);
          setModelFilterOptions(modelList);
          setClusterFilterOptions(clusterLabels);
          setEndpointFilterOptions(endpointList);
          setTotalCount(total);
        }
      } catch (error) {
        if (isCurrent) console.error(error);
      }
      if (isCurrent) setIsGatewayLoaded(true);
    };
    fetchMetrics();
    return () => {
      isCurrent = false;
    };
  }, [currentPage, itemsPerPage, selectedOptions, buildFilterParams]);

  const formatDate = React.useCallback((timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const dateString = date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    const timeString = date
      .toLocaleTimeString("en-US", { hour12: false })
      .split(" ")[0]; // HH:MM:SS
    return `${dateString} ${timeString}`;
  }, []);

  const parseTargetUrl = (target_url) => {
    let modelName = "";
    let clusterLabel = "";
    let clusterDomain = "";
    let endpoint = "";
    if (target_url) {
      try {
        const urlObj = new URL(target_url);
        const targetHostname = urlObj.hostname;
        const targetPathname = urlObj.pathname;
        const hostnameParts = targetHostname.split(".");
        if (hostnameParts.length >= 3) {
          modelName = hostnameParts[0];
          clusterDomain = hostnameParts.slice(1).join(".");
          clusterLabel = CLUSTER_LABEL_MAP[clusterDomain] || clusterDomain;
          endpoint = targetPathname;
        } else {
          modelName = targetHostname;
          clusterLabel = "";
          endpoint = targetPathname;
        }
      } catch (error) {
        modelName = "";
        clusterLabel = "";
        clusterDomain = "";
        endpoint = "";
      }
    }
    return { modelName, clusterLabel, clusterDomain, endpoint };
  };

  // Build filter data for dropdowns
  const buildFilterOptions = (values = []) =>
    Array.from(new Set(values))
      .filter((value) => value !== undefined && value !== null && value !== "")
      .map((value) => ({ name: value }));

  const modelNamesSource =
    modelFilterOptions.length > 0
      ? modelFilterOptions
      : gatewayMetrics.map((item) => item.derivedModelName).filter(Boolean);

  const clusterSource =
    clusterFilterOptions.length > 0
      ? clusterFilterOptions
      : gatewayMetrics.map((item) => item.derivedClusterLabel).filter(Boolean);

  const endpointSource =
    endpointFilterOptions.length > 0
      ? endpointFilterOptions
      : gatewayMetrics.map((item) => item.derivedEndpoint).filter(Boolean);

  const allFilterData = React.useMemo(
    () => ({
      request_asms: buildFilterOptions(allRequestAsms),
      request_client_id: buildFilterOptions(allClientIds),
      request_gmid: buildFilterOptions(allGmids),
      request_method: Array.from(
        new Set(
          gatewayMetrics.map((item) => item.request_method).filter(Boolean),
        ),
      ).map((name) => ({ name })),
      content_type: Array.from(
        new Set(
          gatewayMetrics.map((item) => item.content_type).filter(Boolean),
        ),
      ).map((name) => ({ name })),
      model_name: buildFilterOptions(modelNamesSource),
      cluster: buildFilterOptions(clusterSource),
      endpoint: buildFilterOptions(endpointSource),
      http_status: Array.from(
        new Set(gatewayMetrics.map((item) => item.http_status).filter(Boolean)),
      ).map((name) => ({ name })),
    }),
    [
      allRequestAsms,
      allClientIds,
      allGmids,
      gatewayMetrics,
      modelNamesSource,
      clusterSource,
      endpointSource,
    ],
  );

  // Dropdown filter component
  const FilterDropdown = ({
    title,
    filterData = [],
    selectedOptions,
    setSelectedOptions,
    openDropdown,
    setOpenDropdown,
  }) => {
    const [dropDownFilter, setDropDownFilter] = useState("");
    const isOpen = openDropdown === title;
    const displayedData = (filterData || []).filter((item) =>
      String(item.name || "")
        .toLowerCase()
        .includes(dropDownFilter.toLowerCase()),
    );
    const isFilterActive = selectedOptions[title]?.length > 0;
    const handleCheckboxChange = (key, item) => {
      setSelectedOptions((prev) => ({
        ...prev,
        [key]: prev[key]?.includes(item)
          ? prev[key].filter((i) => i !== item)
          : [...(prev[key] || []), item],
      }));
    };
    const handleClearFilters = () => {
      setSelectedOptions((prev) => ({
        ...prev,
        [title]: [],
      }));
    };
    return (
      <div style={{ position: "relative", display: "inline-block" }}>
        <Dropdown
          align="end"
          show={isOpen}
          drop="down"
          onToggle={(isOpen) => {
            if (isOpen) {
              setOpenDropdown(title);
            } else {
              setOpenDropdown(null);
            }
          }}
        >
          <Dropdown.Toggle
            className="no-caret"
            as="span"
            style={{
              cursor: "pointer",
              marginLeft: "5px",
              fontSize: "1rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "24px",
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (isFilterActive) {
                handleClearFilters();
              } else {
                setOpenDropdown(isOpen ? null : title);
              }
            }}
          >
            <Icon
              name={isFilterActive ? "removeFilter" : "filter"}
              overrides={{
                tooltip: isFilterActive ? "Clear filters" : "Filter table",
                tooltipPlacement: "top",
                iconColor: "#FFFFFF",
                style: { cursor: "pointer" },
              }}
              id={`filter-dropdown-${title}`}
            />
          </Dropdown.Toggle>
          <Dropdown.Menu
            className="dropdown-menu w-100 p-2"
            style={{
              maxHeight: "250px",
              overflow: "auto",
              zIndex: 2000,
              position: "absolute",
              left: 0,
              top: "100%",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Form.Control
              type="text"
              placeholder="Filter..."
              value={dropDownFilter}
              onChange={(e) => setDropDownFilter(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
            <hr className="my-1" />
            <Form.Check
              type="checkbox"
              label="Select All"
              checked={selectedOptions[title]?.length === filterData?.length}
              onChange={(e) => {
                e.stopPropagation();
                setSelectedOptions((prev) => ({
                  ...prev,
                  [title]:
                    prev[title]?.length === filterData?.length
                      ? []
                      : (filterData || []).map((s) => s.name),
                }));
              }}
              className="px-3 ms-1 fw-bold"
            />
            <hr className="my-1" />
            {displayedData.map((item, index) => (
              <Form.Check
                key={index}
                type="checkbox"
                label={item.name}
                checked={selectedOptions[title]?.includes(item.name) || false}
                onChange={(e) => {
                  e.stopPropagation();
                  handleCheckboxChange(title, item.name);
                }}
                className="px-3 ms-1"
              />
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>
    );
  };

  const DateRangeDropDown = ({
    title,
    selectedOptions,
    setSelectedOptions,
    openDropdown,
    setOpenDropdown,
    data,
  }) => {
    const isOpen = openDropdown === title;
    const [tempStartDate, setTempStartDate] = useState(
      selectedOptions[title]?.start || "",
    );
    const [tempEndDate, setTempEndDate] = useState(
      selectedOptions[title]?.end || "",
    );
    // Calculate min and max dates from data
    let minDate = "";
    let maxDate = "";
    if (data && data.length > 0) {
      const dates = data.map((item) => new Date(item.request_time));
      const minDateObj = new Date(Math.min(...dates));
      const maxDateObj = new Date(Math.max(...dates));
      if (!isNaN(minDateObj.getTime()))
        minDate = minDateObj.toISOString().split("T")[0];
      if (!isNaN(maxDateObj.getTime()))
        maxDate = maxDateObj.toISOString().split("T")[0];
    }
    const isFilterActive =
      selectedOptions[title]?.start || selectedOptions[title]?.end;
    const handleApply = () => {
      setSelectedOptions((prev) => ({
        ...prev,
        [title]: { start: tempStartDate, end: tempEndDate },
      }));
      setOpenDropdown(null);
    };
    const handleClearFilters = () => {
      setTempStartDate("");
      setTempEndDate("");
      setSelectedOptions((prev) => ({
        ...prev,
        [title]: { start: "", end: "" },
      }));
    };
    return (
      <div style={{ position: "relative", display: "inline-block" }}>
        <Dropdown
          align="end"
          show={isOpen}
          drop="down"
          onToggle={(isOpen) => {
            if (isOpen) {
              setOpenDropdown(title);
              setTempStartDate(selectedOptions[title]?.start || "");
              setTempEndDate(selectedOptions[title]?.end || "");
            } else {
              setOpenDropdown(null);
            }
          }}
        >
          <Dropdown.Toggle
            className="no-caret"
            as="span"
            style={{
              cursor: "pointer",
              marginLeft: "5px",
              fontSize: "1rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "24px",
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (isFilterActive) {
                handleClearFilters();
              } else {
                setOpenDropdown(isOpen ? null : title);
              }
            }}
          >
            <Icon
              name={isFilterActive ? "removeFilter" : "filter"}
              overrides={{
                tooltip: isFilterActive
                  ? "Clear date filter"
                  : "Filter by date",
                tooltipPlacement: "top",
                iconColor: "#FFFFFF",
                style: { cursor: "pointer" },
              }}
              id={`filter-dropdown-${title}`}
            />
          </Dropdown.Toggle>
          <Dropdown.Menu
            className="dropdown-menu p-3"
            style={{
              minWidth: "280px",
              zIndex: 2000,
              position: "absolute",
              left: 0,
              top: "100%",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ marginBottom: "10px" }}>
              <Form.Label style={{ fontSize: "0.9rem", fontWeight: "500" }}>
                Start Date
              </Form.Label>
              <Form.Control
                type="date"
                value={tempStartDate}
                min={minDate}
                max={maxDate}
                onChange={(e) => setTempStartDate(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div style={{ marginBottom: "15px" }}>
              <Form.Label style={{ fontSize: "0.9rem", fontWeight: "500" }}>
                End Date
              </Form.Label>
              <Form.Control
                type="date"
                value={tempEndDate}
                min={tempStartDate || minDate}
                max={maxDate}
                onChange={(e) => setTempEndDate(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "10px",
              }}
            >
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  handleClearFilters();
                  setOpenDropdown(null);
                }}
                style={{ flex: 1 }}
              >
                Clear
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleApply}
                style={{ flex: 1 }}
              >
                Apply
              </button>
            </div>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    );
  };

  const GatewayMetricsTable = React.useMemo(() => {
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    const indexOfFirstItem =
      totalCount === 0 ? 0 : (currentPage - 1) * itemsPerPage;
    const indexOfLastItem = Math.min(
      indexOfFirstItem + itemsPerPage,
      totalCount,
    );
    const currentItems = gatewayMetrics;
    const getHeaderClass = (key) =>
      openDropdown === key ? "metrics-header-open" : undefined;

    return (
      <Container fluid>
        <div
          className="metrics-table-container"
          style={{ overflowX: "auto", width: "100%" }}
        >
          <Table
            striped
            bordered
            hover
            responsive
            className="metrics-table"
            style={{ minWidth: 1400 }}
          >
            <thead>
              <tr>
                <th className={getHeaderClass("request_time")}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Request Time
                    <span style={{ marginLeft: 4 }}>
                      <DateRangeDropDown
                        title={"request_time"}
                        selectedOptions={selectedOptions}
                        setSelectedOptions={setSelectedOptions}
                        openDropdown={openDropdown}
                        setOpenDropdown={setOpenDropdown}
                        data={gatewayMetrics}
                      />
                    </span>
                  </div>
                </th>
                <th className={getHeaderClass("request_asms")}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    ASMS
                    <span style={{ marginLeft: 4 }}>
                      <FilterDropdown
                        title="request_asms"
                        filterData={allFilterData.request_asms}
                        selectedOptions={selectedOptions}
                        setSelectedOptions={setSelectedOptions}
                        openDropdown={openDropdown}
                        setOpenDropdown={setOpenDropdown}
                      />
                    </span>
                  </div>
                </th>
                <th className={getHeaderClass("request_client_id")}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Client ID
                    <span style={{ marginLeft: 4 }}>
                      <FilterDropdown
                        title="request_client_id"
                        filterData={allFilterData.request_client_id}
                        selectedOptions={selectedOptions}
                        setSelectedOptions={setSelectedOptions}
                        openDropdown={openDropdown}
                        setOpenDropdown={setOpenDropdown}
                      />
                    </span>
                  </div>
                </th>
                <th className={getHeaderClass("request_gmid")}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    GMID
                    <span style={{ marginLeft: 4 }}>
                      <FilterDropdown
                        title="request_gmid"
                        filterData={allFilterData.request_gmid}
                        selectedOptions={selectedOptions}
                        setSelectedOptions={setSelectedOptions}
                        openDropdown={openDropdown}
                        setOpenDropdown={setOpenDropdown}
                      />
                    </span>
                  </div>
                </th>
                <th className={getHeaderClass("request_method")}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Request Method
                  </div>
                </th>
                <th>
                  <div style={{ whiteSpace: "nowrap" }}>Request URL</div>
                </th>
                <th className={getHeaderClass("content_type")}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Content Type
                  </div>
                </th>
                <th>
                  <div style={{ whiteSpace: "nowrap" }}>Content Length</div>
                </th>
                <th>
                  <div style={{ whiteSpace: "nowrap" }}>Target URL</div>
                </th>
                <th className={getHeaderClass("model_name")}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Model Name
                    <span style={{ marginLeft: 4 }}>
                      <FilterDropdown
                        title="model_name"
                        filterData={allFilterData.model_name}
                        selectedOptions={selectedOptions}
                        setSelectedOptions={setSelectedOptions}
                        openDropdown={openDropdown}
                        setOpenDropdown={setOpenDropdown}
                      />
                    </span>
                  </div>
                </th>
                <th className={getHeaderClass("cluster")}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Cluster
                    <span style={{ marginLeft: 4 }}>
                      <FilterDropdown
                        title="cluster"
                        filterData={allFilterData.cluster}
                        selectedOptions={selectedOptions}
                        setSelectedOptions={setSelectedOptions}
                        openDropdown={openDropdown}
                        setOpenDropdown={setOpenDropdown}
                      />
                    </span>
                  </div>
                </th>
                <th className={getHeaderClass("endpoint")}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Endpoint
                    <span style={{ marginLeft: 4 }}>
                      <FilterDropdown
                        title="endpoint"
                        filterData={allFilterData.endpoint}
                        selectedOptions={selectedOptions}
                        setSelectedOptions={setSelectedOptions}
                        openDropdown={openDropdown}
                        setOpenDropdown={setOpenDropdown}
                      />
                    </span>
                  </div>
                </th>
                <th className={getHeaderClass("http_status")}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Http Status
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {!isGatewayLoaded ? (
                <>
                  <tr>
                    <td colSpan={13} className="text-center">
                      <div className="d-flex justify-content-center align-items-center py-4">
                        <Spinner
                          animation="border"
                          role="status"
                          className="me-2"
                        />
                        <span>Loading Metrics...</span>
                      </div>
                    </td>
                  </tr>
                  {/* Hidden row to maintain column widths */}
                  <tr style={{ height: 0, visibility: "hidden" }}>
                    <td
                      style={{ padding: 0, border: "none", minWidth: "120px" }}
                    >
                      &nbsp;
                    </td>
                    <td
                      style={{ padding: 0, border: "none", minWidth: "80px" }}
                    >
                      &nbsp;
                    </td>
                    <td
                      style={{ padding: 0, border: "none", minWidth: "100px" }}
                    >
                      &nbsp;
                    </td>
                    <td
                      style={{ padding: 0, border: "none", minWidth: "80px" }}
                    >
                      &nbsp;
                    </td>
                    <td
                      style={{ padding: 0, border: "none", minWidth: "80px" }}
                    >
                      &nbsp;
                    </td>
                    <td
                      style={{ padding: 0, border: "none", minWidth: "200px" }}
                    >
                      &nbsp;
                    </td>
                    <td
                      style={{ padding: 0, border: "none", minWidth: "120px" }}
                    >
                      &nbsp;
                    </td>
                    <td
                      style={{ padding: 0, border: "none", minWidth: "80px" }}
                    >
                      &nbsp;
                    </td>
                    <td
                      style={{ padding: 0, border: "none", minWidth: "200px" }}
                    >
                      &nbsp;
                    </td>
                    <td
                      style={{ padding: 0, border: "none", minWidth: "120px" }}
                    >
                      &nbsp;
                    </td>
                    <td
                      style={{ padding: 0, border: "none", minWidth: "100px" }}
                    >
                      &nbsp;
                    </td>
                    <td
                      style={{ padding: 0, border: "none", minWidth: "100px" }}
                    >
                      &nbsp;
                    </td>
                    <td
                      style={{ padding: 0, border: "none", minWidth: "80px" }}
                    >
                      &nbsp;
                    </td>
                  </tr>
                </>
              ) : currentItems && currentItems.length > 0 ? (
                currentItems.map((gatewayMetric) => (
                  <tr key={gatewayMetric.id}>
                    <td>{formatDate(gatewayMetric.request_time)}</td>
                    <td>{gatewayMetric.request_asms}</td>
                    <td>{gatewayMetric.request_client_id}</td>
                    <td>{gatewayMetric.request_gmid}</td>
                    <td>{gatewayMetric.request_method}</td>
                    <td>{gatewayMetric.request_url}</td>
                    <td>{gatewayMetric.content_type}</td>
                    <td>{gatewayMetric.content_length}</td>
                    <td>{gatewayMetric.target_url}</td>
                    <td>{gatewayMetric.derivedModelName}</td>
                    <td>{gatewayMetric.derivedClusterLabel}</td>
                    <td>{gatewayMetric.derivedEndpoint}</td>
                    <td>{gatewayMetric.http_status}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={13} className="text-center">
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
        {/* Table Controls */}
        {isGatewayLoaded && (
          <div className="metrics-table-footer">
            <div className="footer-summary">
              Showing {indexOfFirstItem + 1} to{" "}
              {Math.min(indexOfLastItem, totalCount)} of {totalCount} entries
            </div>
            <div
              className="d-flex align-items-center justify-content-end gap-2"
              style={{ flex: 1 }}
            >
              <Form.Select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                style={{ width: 100 }}
                className="mx-2"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </Form.Select>
              <Pagination size="sm" className="mb-0">
                <Pagination.First
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                />
                <Pagination.Prev
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                />
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 &&
                      pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <Pagination.Item
                        key={pageNumber}
                        active={pageNumber === currentPage}
                        onClick={() => setCurrentPage(pageNumber)}
                      >
                        {pageNumber}
                      </Pagination.Item>
                    );
                  } else if (
                    pageNumber === currentPage - 2 ||
                    pageNumber === currentPage + 2
                  ) {
                    return <Pagination.Ellipsis key={pageNumber} disabled />;
                  }
                  return null;
                })}
                <Pagination.Next
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                />
                <Pagination.Last
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                />
              </Pagination>
            </div>
          </div>
        )}
      </Container>
    );
  }, [
    totalCount,
    itemsPerPage,
    currentPage,
    gatewayMetrics,
    openDropdown,
    selectedOptions,
    allFilterData,
    isGatewayLoaded,
    setItemsPerPage,
    setCurrentPage,
    setOpenDropdown,
    setSelectedOptions,
    formatDate,
  ]);

  const GatewayMetricsTableComponent = () => {
    return (
      <Container fluid>
        <Row className="align-items-center mb-3" style={{ minHeight: 56 }}>
          <Col
            xs={12}
            className="d-flex align-items-center justify-content-between gap-2"
          >
            <div className="d-flex align-items-center" style={{ flex: 1 }}>
              <h2
                className="mb-0"
                style={{ fontSize: "2rem", whiteSpace: "nowrap" }}
              >
                Gateway Metrics
              </h2>
            </div>
            <div
              className="d-flex align-items-center justify-content-end gap-2"
              style={{ flex: 1 }}
            >
              <div
                style={{
                  position: "relative",
                  maxWidth: "250px",
                  width: "100%",
                }}
              >
                <Form.Control
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search metrics..."
                  value={globalSearchInput}
                  onChange={(e) => setGlobalSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      triggerSearch();
                    } else if (e.key === "Escape") {
                      clearSearch();
                    }
                  }}
                  style={{
                    paddingRight: globalSearchInput ? "70px" : "38px",
                  }}
                />
                {globalSearchInput && (
                  <button
                    type="button"
                    aria-label="Clear search"
                    onClick={clearSearch}
                    style={{
                      position: "absolute",
                      right: "34px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "transparent",
                      border: "none",
                      padding: 0,
                      margin: 0,
                      width: "26px",
                      height: "26px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                  >
                    <Icon
                      name="close"
                      overrides={{
                        iconColor: "#dc3545",
                        tooltip: "Clear search",
                        tooltipPlacement: "top",
                        style: { pointerEvents: "none" },
                      }}
                      id="global-search-clear"
                    />
                  </button>
                )}
                <button
                  type="button"
                  aria-label="Search"
                  onClick={triggerSearch}
                  style={{
                    position: "absolute",
                    right: "4px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    margin: 0,
                    width: "26px",
                    height: "26px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                  tabIndex={0}
                  disabled={!globalSearchInput.trim()}
                >
                  <Icon
                    name="search"
                    overrides={{
                      iconColor: "#6c757d",
                      tooltip: "Run search",
                      tooltipPlacement: "top",
                      style: { pointerEvents: "none" },
                    }}
                    id="global-search-trigger"
                  />
                </button>
              </div>
              <button
                className="btn btn-primary d-flex align-items-center ms-2"
                onClick={handleExport}
              >
                <i className="fas fa-download me-2"></i>
                Export Metrics
              </button>
              <ExportToCSV ref={exportRef} metrics={"gateway"} />
            </div>
          </Col>
        </Row>
        {GatewayMetricsTable}
      </Container>
    );
  };

  const TGIMetricsTable = () => {
    const sortedTgiMetrics = [...tgiMetrics].sort(
      (a, b) => new Date(b.request_time) - new Date(a.request_time),
    );
    const totalPages = Math.ceil(sortedTgiMetrics.length / tgiItemsPerPage);
    const indexOfLastItem = tgiCurrentPage * tgiItemsPerPage;
    const indexOfFirstItem = indexOfLastItem - tgiItemsPerPage;
    const currentItems = sortedTgiMetrics.slice(
      indexOfFirstItem,
      indexOfLastItem,
    );
    return (
      <Container fluid>
        <Row className="align-items-center mb-3" style={{ minHeight: 56 }}>
          <Col
            xs={12}
            className="d-flex align-items-center justify-content-between gap-2"
          >
            <div className="d-flex align-items-center" style={{ flex: 1 }}>
              <h2
                className="mb-0"
                style={{ fontSize: "2rem", whiteSpace: "nowrap" }}
              >
                TGI Metrics
              </h2>
            </div>
          </Col>
        </Row>
        <div className="metrics-table-container">
          <Table
            className="metrics-table table-striped"
            style={{ minWidth: 1200 }}
          >
            <thead>
              <tr>
                <th>Request Time</th>
                <th>Model</th>
                <th>Pod Name</th>
                <th>Compute Type</th>
                <th>Total Time (ms)</th>
                <th>Validation Time (ms)</th>
                <th>Inference Time (ms)</th>
                <th>Time per Token (ms)</th>
                <th>Prompt Tokens</th>
                <th>Generated Tokens</th>
                <th>Queue Time (ms)</th>
                <th>ASMS</th>
              </tr>
            </thead>
            <tbody>
              {!isTgiLoaded ? (
                <tr>
                  <td colSpan={12} className="text-center">
                    <div className="d-flex justify-content-center align-items-center py-4">
                      <Spinner
                        animation="border"
                        role="status"
                        className="me-2"
                      />
                      <span>Loading Metrics...</span>
                    </div>
                  </td>
                </tr>
              ) : currentItems && currentItems.length > 0 ? (
                currentItems.map((metric) => (
                  <tr key={metric.id}>
                    <td>{formatDate(metric.request_time)}</td>
                    <td>{metric.model_name}</td>
                    <td>{metric.pod_name}</td>
                    <td>{metric.compute_type}</td>
                    <td>{metric.total_time}</td>
                    <td>{metric.validation_time}</td>
                    <td>{metric.inference_time}</td>
                    <td>{metric.time_per_token}</td>
                    <td>{metric.prompt_tokens}</td>
                    <td>{metric.generated_tokens}</td>
                    <td>{metric.queue_time}</td>
                    <td>{metric.request_asms}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={12} className="text-center">
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        {/* Table Controls */}
        {isTgiLoaded && (
          <div className="metrics-table-footer">
            <div className="footer-summary">
              Showing {indexOfFirstItem + 1} to{" "}
              {Math.min(indexOfLastItem, tgiMetrics.length)} of{" "}
              {tgiMetrics.length} entries
            </div>
            <div
              className="d-flex align-items-center justify-content-end gap-2"
              style={{ flex: 1 }}
            >
              <Form.Select
                value={tgiItemsPerPage}
                onChange={(e) => setTgiItemsPerPage(Number(e.target.value))}
                style={{ width: 100 }}
                className="mx-2"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </Form.Select>
              <Pagination size="sm" className="mb-0">
                <Pagination.First
                  onClick={() => setTgiCurrentPage(1)}
                  disabled={tgiCurrentPage === 1}
                />
                <Pagination.Prev
                  onClick={() => setTgiCurrentPage(tgiCurrentPage - 1)}
                  disabled={tgiCurrentPage === 1}
                />
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= tgiCurrentPage - 1 &&
                      pageNumber <= tgiCurrentPage + 1)
                  ) {
                    return (
                      <Pagination.Item
                        key={pageNumber}
                        active={pageNumber === tgiCurrentPage}
                        onClick={() => setTgiCurrentPage(pageNumber)}
                      >
                        {pageNumber}
                      </Pagination.Item>
                    );
                  } else if (
                    pageNumber === tgiCurrentPage - 2 ||
                    pageNumber === tgiCurrentPage + 2
                  ) {
                    return <Pagination.Ellipsis key={pageNumber} disabled />;
                  }
                  return null;
                })}
                <Pagination.Next
                  onClick={() => setTgiCurrentPage(tgiCurrentPage + 1)}
                  disabled={tgiCurrentPage === totalPages}
                />
                <Pagination.Last
                  onClick={() => setTgiCurrentPage(totalPages)}
                  disabled={tgiCurrentPage === totalPages}
                />
              </Pagination>
            </div>
          </div>
        )}
      </Container>
    );
  };

  return (
    <>
      <Container
        fluid
        className="title text-center py-2 mb-2"
        style={{
          backgroundColor: "#f8f9fa",
          borderBottom: "1px solid #dee2e6",
          marginTop: "0.5rem",
        }}
      >
        <h4 className="m-0">Metrics</h4>
      </Container>
      <Container fluid className="text-center">
        <Tabs
          id="metrics-tabs"
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k || "graphs")}
          className="mb-3"
        >
          <Tab eventKey="graphs" title="Graph View">
            <MetricsVisual />
          </Tab>

          <Tab eventKey="gateway-table" title="Gateway Metrics">
            <GatewayMetricsTableComponent />
          </Tab>

          <Tab eventKey="tgi-table" title="TGI Metrics">
            <TGIMetricsTable />
          </Tab>
        </Tabs>
      </Container>
    </>
  );
}

export default Metrics;
